import type { I18n, MessageDescriptor } from '@lingui/core'
import { msg } from '@lingui/core/macro'
import type {
  CatalogAssetKind,
  CatalogCategory,
  CatalogEnrichment,
  CatalogImageKind,
  CatalogMarket,
  CatalogMatchSource,
  CatalogOptionKind,
  CatalogProductStatus,
  CatalogSourceRole,
} from '~convex/lib/catalogEnums'
import {
  CATALOG_CATEGORIES,
  CATALOG_ENRICHMENTS,
  CATALOG_PRODUCT_STATUSES,
} from '~convex/lib/catalogEnums'

// Bilingual labels for the closed catalog enums the admin surface renders.
// The enums themselves come from `convex/lib/catalogEnums.ts`; only the
// human-readable half lives here (precedent: `admin-activity.ts`).

const CATEGORY_LABEL: Record<CatalogCategory, MessageDescriptor> = {
  appliances: msg`Appliances`,
  commercial: msg`Commercial`,
  construction: msg`Construction`,
  decor: msg`Decor`,
  doors_and_windows: msg`Doors & windows`,
  fixtures_and_fittings: msg`Fixtures & fittings`,
  furniture: msg`Furniture`,
  kids: msg`Kids`,
  lighting: msg`Lighting`,
  office: msg`Office`,
  other: msg`Other`,
  plants: msg`Plants`,
  surfaces: msg`Surfaces`,
}

const STATUS_LABEL: Record<CatalogProductStatus, MessageDescriptor> = {
  active: msg`Active`,
  discontinued: msg`Discontinued`,
  hidden: msg`Hidden`,
}

const ENRICHMENT_LABEL: Record<CatalogEnrichment, MessageDescriptor> = {
  partial: msg`Partial`,
  rich: msg`Rich`,
  thin: msg`Thin`,
}

const ROLE_LABEL: Record<CatalogSourceRole, MessageDescriptor> = {
  aggregator: msg`Aggregator`,
  brand: msg`Brand`,
  manual: msg`Manual`,
  retailer: msg`Retailer`,
}

const MARKET_LABEL: Record<CatalogMarket, MessageDescriptor> = {
  br: msg`Brazil`,
  eu: msg`Europe`,
  global: msg`Global`,
  other: msg`Other market`,
  us: msg`United States`,
}

const IMAGE_KIND_LABEL: Record<CatalogImageKind, MessageDescriptor> = {
  ambient: msg`Ambient`,
  detail: msg`Detail`,
  drawing: msg`Drawing`,
  main: msg`Main`,
  swatch: msg`Swatch`,
}

const ASSET_KIND_LABEL: Record<CatalogAssetKind, MessageDescriptor> = {
  archicad: msg`Archicad`,
  autocad: msg`AutoCAD`,
  ies: msg`IES photometry`,
  pdf_catalog: msg`PDF catalog`,
  revit: msg`Revit`,
  sketchup: msg`SketchUp`,
  technical_sheet: msg`Technical sheet`,
}

const OPTION_KIND_LABEL: Record<CatalogOptionKind, MessageDescriptor> = {
  finish: msg`Finish`,
  module: msg`Module`,
  other: msg`Other option`,
  size: msg`Size`,
}

const MATCH_LABEL: Record<CatalogMatchSource, MessageDescriptor> = {
  exact: msg`Exact match`,
  fuzzy: msg`Fuzzy match`,
  manual: msg`Confirmed`,
}

export const CATALOG_CATEGORY_ORDER = CATALOG_CATEGORIES
export const CATALOG_STATUS_ORDER = CATALOG_PRODUCT_STATUSES
export const CATALOG_ENRICHMENT_ORDER = CATALOG_ENRICHMENTS

export const catalogCategoryLabel = (i18n: I18n, category: CatalogCategory) =>
  i18n._(CATEGORY_LABEL[category])
export const catalogStatusLabel = (i18n: I18n, status: CatalogProductStatus) =>
  i18n._(STATUS_LABEL[status])
export const catalogEnrichmentLabel = (i18n: I18n, enrichment: CatalogEnrichment) =>
  i18n._(ENRICHMENT_LABEL[enrichment])
export const catalogRoleLabel = (i18n: I18n, role: CatalogSourceRole) => i18n._(ROLE_LABEL[role])
export const catalogMarketLabel = (i18n: I18n, market: CatalogMarket) =>
  i18n._(MARKET_LABEL[market])
export const catalogImageKindLabel = (i18n: I18n, kind: CatalogImageKind) =>
  i18n._(IMAGE_KIND_LABEL[kind])
export const catalogAssetKindLabel = (i18n: I18n, kind: CatalogAssetKind) =>
  i18n._(ASSET_KIND_LABEL[kind])
export const catalogOptionKindLabel = (i18n: I18n, kind: CatalogOptionKind) =>
  i18n._(OPTION_KIND_LABEL[kind])
export const catalogMatchLabel = (i18n: I18n, source: CatalogMatchSource) =>
  i18n._(MATCH_LABEL[source])

/** Short upper-case market tag for dense rows: `BR`, `US`, `EU`, `GL`, `··`. */
export const catalogMarketTag = (market: CatalogMarket): string =>
  ({ br: 'BR', eu: 'EU', global: 'GL', other: '··', us: 'US' })[market]

/**
 * The one-letter dimension prefixes. They read as Portuguese initials
 * (Largura, Profundidade, Altura, Comprimento) and were hard-coded, so an
 * English reader saw `L 80 × P 90` where they should see `W 80 × D 90`.
 * `Ø` is a symbol and stays as it is in both locales.
 */
const DIMENSION_LABEL = {
  depth: msg`D`,
  height: msg`H`,
  length: msg`L`,
  width: msg`W`,
} as const satisfies Record<string, MessageDescriptor>

type CatalogDimensions = {
  depth?: number
  diameter?: number
  height?: number
  length?: number
  width?: number
}

/**
 * `W 80 × D 90 × H 75 cm`, in the reader's locale. Shared by every catalog
 * surface that shows measurements, so the two copies cannot drift.
 */
export const catalogDimensionsText = (i18n: I18n, dimensions: CatalogDimensions): string => {
  const parts = [
    dimensions.width === undefined ? null : `${i18n._(DIMENSION_LABEL.width)} ${dimensions.width}`,
    dimensions.depth === undefined ? null : `${i18n._(DIMENSION_LABEL.depth)} ${dimensions.depth}`,
    dimensions.height === undefined
      ? null
      : `${i18n._(DIMENSION_LABEL.height)} ${dimensions.height}`,
    dimensions.length === undefined
      ? null
      : `${i18n._(DIMENSION_LABEL.length)} ${dimensions.length}`,
    dimensions.diameter === undefined ? null : `Ø ${dimensions.diameter}`,
  ].filter((part) => part !== null)
  return parts.join(' × ')
}

// `some` rather than `includes`, which would want a `T` and force the list to
// be widened with an assertion just to be read.
const isOneOf = <T extends string>(list: readonly T[], value: unknown): value is T =>
  typeof value === 'string' && list.some((item) => item === value)

export const isCatalogCategory = (value: unknown): value is CatalogCategory =>
  isOneOf(CATALOG_CATEGORIES, value)
export const isCatalogStatus = (value: unknown): value is CatalogProductStatus =>
  isOneOf(CATALOG_PRODUCT_STATUSES, value)
export const isCatalogEnrichment = (value: unknown): value is CatalogEnrichment =>
  isOneOf(CATALOG_ENRICHMENTS, value)
